import { firstValueFrom } from 'rxjs'
import { Renderer } from 'instantsearch.js/es/types'
import connectHierarchicalMenu, {
    HierarchicalMenuConnectorParams as CParams,
    HierarchicalMenuItem,
    HierarchicalMenuWidgetDescription as WDescription,
} from 'instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu'
import { TranslateService } from '@ngx-translate/core'

import {
    Component,
    forwardRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { CategoryFragment } from '@app-graphql/schema'
import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { AbstractInstantsearchWidgetComponent } from '@app-domains/algolia/abstract-instantsearch-widget.component'
import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'

@Component({
    selector: 'app-categories-refinement-list',
    templateUrl: './categories-refinement-list.component.html',
})
export class CategoriesRefinementListComponent extends AbstractInstantsearchWidgetComponent<
WDescription,
CParams
> implements OnInit, OnChanges, OnDestroy {

    public override readonly widgetName = 'RefinementList'

    private attribute = 'categories'

    private attributes = [
        'categoryHierarchy.lvl0',
        'categoryHierarchy.lvl1',
    ]

    @Input()
    public localisedAttribute: string

    @Input()
    public category?: CategoryFragment

    public isShowingMore: boolean = false
    private rootPath: string | undefined

    constructor(
        @Inject(forwardRef(() => AlgoliaInstantsearchComponent))
        public instantSearchInstance: AlgoliaInstantsearchComponent,
        private route: ActivatedRoute,
        private router: Router,
        private translateService: TranslateService,
        private localization: LocalizationService,
        private analytics: AnalyticsService,
    ) {
        super()
    }

    public override ngOnInit(): void {
        this.initialiseWidget(this.category)
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.category && ! changes.category.isFirstChange()) {
            this.initialiseWidget(changes.category.currentValue)
        }
    }

    public initialiseWidget(category?: CategoryFragment): void {
        this.rootPath = category
            ? category.translations[this.localization.getCurrentLocale()].name
            : undefined

        this.createWidget(connectHierarchicalMenu, {
            attributes: this.attributes,
            sortBy: [
                'isRefined:desc',
                'count:desc',
            ],
            limit: 3,
            rootPath: this.rootPath,
            showMore: this.isShowingMore,
            showParentLevel: false,
            showMoreLimit: 20,
        })

        super.ngOnInit()
    }

    private async initialiseRefinementFromUrl(): Promise<void> {
        const queryParams = await firstValueFrom(this.route.queryParamMap)
        const refinementValues = queryParams.get(
            this.localisedAttribute.toLowerCase() ?? this.attribute,
        )?.split(',') ?? []
        refinementValues.forEach((v) => this.state!.refine(v))
    }

    public toggleItem(item: string): void {
        const params = { ...this.route.snapshot.queryParams }
        const previousRefinement = this.state?.items?.find((i) => i.isRefined)

        if (params.categories) {
            delete params.categories
            void this.router.navigate([], { queryParams: params })
            this.state?.refine(item)
            this.previousRefinement(previousRefinement)
            return
        }

        this.state?.refine(item)
        this.setUrlPart(item)
        this.previousRefinement(previousRefinement)
    }

    private previousRefinement(previousRefinement: HierarchicalMenuItem | undefined) {
        if (previousRefinement) {
            const cat = this.translateService.instant('catalog-index.refinement-lists.category')
            this.analytics.logFilterOff('checkbox', cat, previousRefinement.value)
        }
    }

    private setUrlPart(item: string): void {
        const flattenedItems = [
            ...this.state?.items ?? [],
            ...(this.state?.items ?? []).reduce((acc, val) => {
                return [
                    ...acc,
                    ...val.data ?? [],
                ]
            }, [] as HierarchicalMenuItem[]),
        ]

        void this.router.navigate(
            [],
            {
                relativeTo: this.route,
                queryParams: {
                    [this.localisedAttribute.toLowerCase() ?? this.attribute]: flattenedItems
                        .filter((i) => item.includes(' > ')
                            ? item === i.value || ! i.value.includes(' > ')
                            : true,
                        )
                        .filter((i) => i.value === item ? ! i.isRefined : i.isRefined)
                        .map((i) => i.value).join(',') || undefined,
                },
                queryParamsHandling: 'merge', // remove to replace all query params by provided
            },
        )
    }

    public override updateState: Renderer<WDescription['renderState'], CParams> = (state, isFirstRendering): void => {
        if (isFirstRendering) {
            Promise.resolve().then(() => {
                this.state = state

                if (
                    this.category
                    && this.instantSearchInstance.instantSearchInstance.helper?.state.hierarchicalFacetsRefinements[
                        this.attributes[0]].length === 0
                    && this.rootPath
                ) {
                    this.state.refine(this.rootPath)
                }

                void this.initialiseRefinementFromUrl()
            })
        } else {
            this.state = state
        }
    }

    public override ngOnDestroy(): void {
        if (this.widget) {
            this.instantSearchInstance.instantSearchInstance.removeWidgets([this.widget])
        }

        super.ngOnDestroy()
    }

}
